import { Check, Zap } from "lucide-react";
import { useMembership } from "../hooks/use-membership";
import { SUBSCRIPTION_PLANS, type SubscriptionTier } from "../types/subscription";
import { TopNav } from "../components/layout/top-nav";

export function MembershipPage() {
  const { currentPlan, upgradeToTier, isLoaded } = useMembership();

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const tiers: SubscriptionTier[] = ["free", "basic", "pro", "ultra"];

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNav user={null} />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              选择你的会员套餐
            </h1>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              解锁全部 AI 能力，开启智能对话新时代
            </p>
          </div>

          {/* Current Plan Banner */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 mb-12 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">当前套餐</p>
                <p className="text-2xl font-bold">{currentPlan.name}</p>
              </div>
              {currentPlan.id !== "ultra" && (
                <button className="px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors">
                  升级套餐
                </button>
              )}
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier) => {
              const plan = SUBSCRIPTION_PLANS[tier];
              const isCurrent = currentPlan.id === tier;
              const isPopular = tier === "pro";

              return (
                <div
                  key={tier}
                  className={`relative bg-white rounded-2xl p-6 border-2 transition-all ${
                    isCurrent
                      ? "border-blue-500 shadow-lg"
                      : "border-gray-200 hover:border-gray-300"
                  } ${isPopular ? "ring-2 ring-purple-500 ring-offset-2" : ""}`}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-purple-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
                        最受欢迎
                      </span>
                    </div>
                  )}

                  {/* Plan Name */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-gray-500 ml-2">/月</span>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  {isCurrent ? (
                    <button
                      disabled
                      className="w-full py-3 bg-gray-100 text-gray-400 rounded-xl font-semibold cursor-not-allowed"
                    >
                      当前套餐
                    </button>
                  ) : (
                    <button
                      onClick={() => upgradeToTier(tier)}
                      className={`w-full py-3 rounded-xl font-semibold transition-colors ${
                        tier === "free"
                          ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      {plan.price === 0 ? "选择免费版" : `升级到 ${plan.name}`}
                    </button>
                  )}

                  {/* Current Plan Indicator */}
                  {isCurrent && (
                    <div className="absolute top-6 right-6">
                      <span className="bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-1 rounded">
                        当前
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Usage Stats */}
          <div className="mt-12 bg-white rounded-2xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              今日使用情况
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <UsageCard
                title="DeepSeek 对话"
                used={0}
                limit={currentPlan.limits.deepseek}
              />
              <UsageCard
                title="GPT-4o 对话"
                used={0}
                limit={currentPlan.limits.gpt4o}
              />
              <UsageCard
                title="图片生成"
                used={0}
                limit={currentPlan.limits.imageGeneration}
              />
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm">
              如有疑问，请联系客服：support@persona.codes
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

// 使用量卡片组件
function UsageCard({
  title,
  used,
  limit,
}: {
  title: string;
  used: number;
  limit: number | "unlimited";
}) {
  const percentage = limit === "unlimited" ? 0 : (used / limit) * 100;
  const displayLimit = limit === "unlimited" ? "无限" : limit;

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">{title}</span>
        <span className="text-sm text-gray-500">
          {used} / {displayLimit}
        </span>
      </div>
      {limit !== "unlimited" && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all"
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>
      )}
      {limit === "unlimited" && (
        <div className="flex items-center gap-2 text-green-600">
          <Zap className="w-4 h-4" />
          <span className="text-sm font-medium">无限使用</span>
        </div>
      )}
    </div>
  );
}
